import { usePDFPrintHook } from "@careinfo/layout";
import { tw } from "@careinfo/tailwind";
import loadable from "@loadable/component";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../components/Reusable/Spinner";
import APIS from "../../constant/Endpoint";
import useGetHook from "../../customHooks/useGetHook";
import usePostHook from "../../customHooks/usePostHook";
import {
  patchApiData,
  patchApiFormData,
  postApiData,
} from "../../helpers/AxiosInstance";

const EditJournalEntryComponent = loadable(
  () => import("../../components/JournalEntry/Edit")
);

export default () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isRevertLoading, setRevertLoading] = useState(false);
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading: isDataLoading } = useGetHook({
    url: `${APIS.JOURNAL_ENTRY}${id}/`,
    queryKey: ["journal-entry", `journal-entry-${id}`],
    params: {},
  });

  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["ledger-account"],
    navigateURL: "",
  });
  const handleCreateAccount = async (
    values: any,
    setOpenAccountPopup: any,
    setError: any
  ) => {
    const url = APIS.COA;
    const formData = {
      ...values,

      is_group: false,
    };
    try {
      const res = await mutateAsync({ url, formData, setError });
      if (res?.status === 201) {
        setOpenAccountPopup(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = `${APIS.JOURNAL_ENTRY}${id}/`;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);

    if (values?.journal_account) {
      formData.append(
        "journal_account",
        JSON.stringify(values.journal_account)
      );
    }
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    values?.narration ? formData.append("narration", values?.narration) : null;
    try {
      const res = await patchApiFormData({ url, formData, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["journal-entry"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/journal-voucher");
        }
        toast.success("Updated Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Journal Voucher",
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = async () => {
    const url = `${APIS.JOURNAL_ENTRY}${id}/`;
    const formData = {
          status: "Cancelled", 
    };
    try {
      patchApiData({ url, formData, setError: () => {} });
    } catch (err) {
      console.error(err);
    }
  };

  const handleRevert = async (setError) => {
    const url = `${APIS.BILL}${id}/revert/`;
    setRevertLoading(true);
    try {
      const res = await postApiData({ url, formData: {}, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["journal-entry"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevertLoading(false);
    }
  };

  return (
    <div>
      <div className={tw`flex items-center justify-between mb-2`}>
        <h1 className={tw`font-semibold text-2xl leading-8`}>
          Edit Journal Voucher
        </h1>
      </div>
      {isDataLoading ? (
        <Spinner />
      ) : (
        <EditJournalEntryComponent
          handleEdit={handleEdit}
          isLoading={isLoading}
          data={data}
          openPrintPopup={openPrintPopup}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          isPrintLoading={isPrintLoading}
          handleRevert={handleRevert}
          handleCancel={handleCancel}
          isRevertLoading={isRevertLoading}
          handleCreateAccount={handleCreateAccount}
          isPostLoading={isPostLoading}
        />
      )}
    </div>
  );
};
